/** Family 5: the free layout — independent shelves and uprights, spine shelves (taxonomy §1.5; pilot-v1 R2-07). */
import { frontsParameterDef } from '../fronts';
import jointsJson from './joints.json' with { type: 'json' };
import type { Family } from '../registry-types';
import type { FamilyDefinition } from '../types';
import { mm } from '../../domain/units';
import { baseParameterDefs, setBaseParam } from '../base';
import { generateFree } from './generate';
import { checkFreeParams, FREE_INTAKE, freeDesignFromFit, freeFitSummary, type FreeFitParams } from './intake';
import { createFree, FREE_ID, FREE_LABEL, FREE_PRESETS, FREE_VERSION, freeParamsOf, setFreeParam, type FreePresetKey } from './design';

export const FREE_DEFINITION: FamilyDefinition = Object.freeze({
  id: FREE_ID,
  version: FREE_VERSION,
  label: FREE_LABEL,
  description: 'Shelves and uprights placed one by one: irregular compartments, spine and E-shaped shelves.',
  parameters: [
    { key: 'W', type: 'length', fieldKey: 'W', range: { min: mm(300), max: mm(2400), step: 10 }, default: mm(720), unit: 'mm', critical: true, ui: { label: 'Width', control: 'field', group: 'Overall size' } },
    { key: 'H', type: 'length', fieldKey: 'H', range: { min: mm(300), max: mm(2200), step: 10 }, default: mm(900), unit: 'mm', critical: true, ui: { label: 'Height', control: 'field', group: 'Overall size' } },
    { key: 'D', type: 'length', fieldKey: 'D', range: { min: mm(150), max: mm(450), step: 10 }, default: mm(260), unit: 'mm', critical: true, ui: { label: 'Depth', control: 'field', group: 'Overall size' } },
    { key: 'shelves', type: 'list', default: null, critical: false, ui: { label: 'Shelves', help: 'Bottom to top: the height of each board and where it starts and ends. Ends that meet an upright join it.', control: 'boards', group: 'Boards' } },
    { key: 'backs', type: 'list', default: null, critical: false, ui: { label: 'Back boards', help: 'Boards in the back plane, behind some compartments or rising above a shelf. With any back board, the shelves stand one board forward.', control: 'boards', group: 'Boards' } },
    { key: 'uprights', type: 'list', default: null, critical: false, ui: { label: 'Uprights', help: 'Left to right: where each upright stands and where it starts and ends. A shelf that runs across an upright cuts it.', control: 'boards', group: 'Boards' } },
    ...baseParameterDefs('Base', { sides: false }).map((p) => (p.key === 'base' ? { ...p, options: p.options!.filter((o) => o.value !== 'plinth') } : p)).filter((p) => p.key !== 'plinth' && p.key !== 'plinthSetback'),
    frontsParameterDef(),
    {
      key: 'stock', type: 'enum', fieldKey: 'stockThickness', default: 'birch-ply-18', critical: true,
      options: [
        { value: 'birch-ply-18', label: 'Birch plywood 18 mm' },
        { value: 'sheesham-board-25', label: 'Sheesham 25 mm' },
        { value: 'oak-board-20', label: 'Oak 20 mm' },
        { value: 'sheesham-board-38', label: 'Sheesham 38 mm' },
        { value: 'sheesham-board-45', label: 'Sheesham 45 mm' },
        { value: 'oak-board-38', label: 'Oak 38 mm' },
        { value: 'oak-board-45', label: 'Oak 45 mm' },
        { value: 'sheesham-board-70', label: 'Sheesham 70 mm slab' },
        { value: 'oak-board-70', label: 'Oak 70 mm slab' },
      ],
      ui: { label: 'Material', help: 'Chosen in Look.', control: 'choice', group: 'Construction', step: 'look' },
    },
  ],
  presets: (Object.entries(FREE_PRESETS) as [string, (typeof FREE_PRESETS)[FreePresetKey]][]).map(([id, p]) => ({ id, label: p.label, description: p.description, params: { W: p.W, H: p.H, D: p.D, ...p.params } })),
  handles: [],
}) as FamilyDefinition;

export const FREE_FAMILY: Family<FreeFitParams> = {
  definition: FREE_DEFINITION,
  jointsJson,
  intake: FREE_INTAKE,
  generate: (design, stock, construction) => generateFree(design, stock, construction),
  checkParams: checkFreeParams,
  designFromFit: freeDesignFromFit,
  fitSummary: freeFitSummary,
  create: (presetId?: string) => createFree((presetId as FreePresetKey) ?? 'cubbies'),
  setParam: (design, key, value) => {
    const b = setBaseParam(freeParamsOf(design), key, value);
    if (b) return { ...design, appearance: { ...design.appearance }, params: b as unknown as Record<string, unknown> };
    return setFreeParam(design, key, value);
  },
};

export * from './design';
export * from './generate';
export * from './intake';
